import React from 'react';

import {
	type ExtensionManifest,
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI, CorePlugin, NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type CardClient from '@atlaskit/link-provider/client';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import { ArtifactsExperienceManifest } from '@atlassian/native-embeds-artifacts-experience';
import { AvpvizExperienceManifest } from '@atlassian/native-embeds-avpviz-experience';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
import type { NativeEmbedAnalyticsPageContext } from '@atlassian/native-embeds-core/embed-frame-ui';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types';
import { DBExperienceManifest } from '@atlassian/native-embeds-db-experience';
import { DBEntryExperienceManifest } from '@atlassian/native-embeds-db-experience/entry-experience';
import {
	MauiExperienceManifest,
	MAUI_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-maui-experience';
import {
	MauiWhiteboardExperienceManifest,
	MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-maui-whiteboard-experience';
import { PageExperienceManifest } from '@atlassian/native-embeds-page-experience';
import { SlideExperienceManifest } from '@atlassian/native-embeds-slide-experience';
import { WhiteboardExperienceManifest } from '@atlassian/native-embeds-whiteboard-experience';

import type { NativeEmbedOverlayRenderer } from './overlay';
/** Minimal local mirror of AI plugin shared state to avoid a hard dep on `@atlassian/editor-plugin-ai`. */
type AIExperienceSharedState = {
	aiDecorationExperiencePluginState: {
		inlineRovoStreamingTargetId?: string | null;
	};
};

type AIExperiencePluginType = NextEditorPlugin<
	'aiExperience',
	{ sharedState: AIExperienceSharedState | undefined }
>;

type EditorViewModePluginType = NextEditorPlugin<
	'editorViewMode',
	{
		sharedState:
			| {
					mode?: 'edit' | 'view';
			  }
			| undefined;
	}
>;

type NativeEmbedsEditorAPI = PublicPluginAPI<
	[CorePlugin, LocalIdPlugin, AIExperiencePluginType, MalleableUiPlugin, EditorViewModePluginType]
>;

/**
 * IDs of the MAUI-specific experiences (the AI "Remix"/malleable diagram flow).
 *
 * These are gated on the `cc-maui-experiment` experiment rather than the
 * product-specific non-MAUI rollout gates, so that a rollback of
 * `cc-maui-experiment` disables the MAUI experiences without affecting generic
 * native embeds (Pages, Databases, Slides, non-MAUI Whiteboards).
 *
 * `MauiWhiteboardExperienceManifest` only matches whiteboard URLs with
 * `isRemixDiagram=true` (i.e. diagrams created via the MAUI flow). When it is
 * disabled, such URLs fall through to the generic `WhiteboardExperienceManifest`
 * (which matches the same whiteboard path without the `isRemixDiagram` param), so
 * the board still renders as a plain native-embed whiteboard.
 */
const mauiExperienceIds = new Set([
	MAUI_EXPERIENCE_MANIFEST_ID,
	MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID,
]);

const allExperiences = [
	ArtifactsExperienceManifest,
	AvpvizExperienceManifest,
	DBEntryExperienceManifest,
	DBExperienceManifest,
	MauiExperienceManifest,
	PageExperienceManifest,
	SlideExperienceManifest,
	// MauiWhiteboardExperienceManifest must come before WhiteboardExperienceManifest
	// so its more specific isUrlMatch (requiring isRemixDiagram=true) is checked first.
	MauiWhiteboardExperienceManifest,
	WhiteboardExperienceManifest,
] satisfies [ExperienceManifest, ...ExperienceManifest[]];

const gatedExperiences = allExperiences.map((experience: ExperienceManifest) => ({
	...experience,
	isUrlMatch: (url: string): boolean => {
		if (mauiExperienceIds.has(experience.id)) {
			return (
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
				experience.isUrlMatch(url)
			);
		}
		// Feature-flag names must remain static literals for static feature-flag tooling.
		return (
			(fg('platform_native_embeds_rollout_non_maui_experience') ||
				fg('platform_native_embeds_rollout_jira_non_maui')) &&
			experience.isUrlMatch(url)
		);
	},
})) as [ExperienceManifest, ...ExperienceManifest[]];

// Register all experience manifests at module-load time.
// This is intentionally module-level (not inside a React lifecycle) because the
// facade must be available before any child embed loads.
getNativeEmbedFacade().setup({
	experiences: gatedExperiences,
});

const createRenderFn =
	(
		isPublishedPage: boolean,
		api?: NativeEmbedsEditorAPI,
		renderOverlay?: NativeEmbedOverlayRenderer,
		analyticsPageContext?: NativeEmbedAnalyticsPageContext,
	) =>
	async () => {
		const { default: Ui } = await import(
			/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'
		);
		return (props: React.ComponentProps<typeof Ui>) => (
			<Ui
				{...props}
				api={api}
				isPublishedPage={isPublishedPage}
				renderOverlay={renderOverlay}
				analyticsPageContext={analyticsPageContext}
			/>
		);
	};

const createManifest = (
	isPublishedPage: boolean,
	api?: NativeEmbedsEditorAPI,
	cardClient?: CardClient,
	renderOverlay?: NativeEmbedOverlayRenderer,
	analyticsPageContext?: NativeEmbedAnalyticsPageContext,
): ExtensionManifest => {
	// Wire up the CardClient for short-link URL resolution when provided.
	// This is a separate call from setup() because the CardClient may not be
	// available at module-load time when setup() is called.
	if (cardClient) {
		getNativeEmbedFacade().setCardClient(cardClient);
	}

	return {
		title: 'Native Embed',
		type: NATIVE_EMBED_EXTENSION_TYPE,
		key: NATIVE_EMBED_EXTENSION_KEY,
		description: 'Embed native Atlassian content',
		modules: {
			nodes: {
				default: {
					type: 'extension',
					render: createRenderFn(isPublishedPage, api, renderOverlay, analyticsPageContext),
					// @ts-expect-error This option is experimental and not reflected in the manifest type definition
					__hideFrame: true,
				},
				...Object.fromEntries(
					getNativeEmbedFacade()
						.getAllExperienceIds()
						.map((experienceId) => [
							experienceId,
							{
								type: 'extension',
								render: createRenderFn(isPublishedPage, api, renderOverlay, analyticsPageContext),
								__hideFrame: true,
							},
						]),
				),
			},
		},
	};
};

export { createManifest };
